import type { Break, Today } from "@/features/attendance/api";

/* Breaks during the day: an hour at least (Art. 118). Time past the allowance is flagged for HR
   to look at; it is never deducted. */

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60_000;
const SECONDS_PER_MINUTE = 60;

/** The allowance when the server did not say (the law's hour). */
export const DEFAULT_BREAK_ALLOWANCE = 60;

interface Span {
  start: number;
  end: number;
}

function spanOf(entry: { started_at: string; ended_at?: string | null }, now: number): Span {
  return {
    start: Date.parse(entry.started_at),
    end: entry.ended_at ? Date.parse(entry.ended_at) : now,
  };
}

function lengthOf(span: Span): number {
  return Math.max(0, span.end - span.start);
}

/** Whole minutes of one break, an open one counted up to `now`. */
export function breakMinutes(entry: Pick<Break, "started_at" | "ended_at">, now: number): number {
  return Math.floor(lengthOf(spanOf(entry, now)) / MS_PER_MINUTE);
}

/**
 * Regular break seconds used today, live while a break is open: every break of the day adds
 * up, so a second break carries on from where the first one stopped. A nursing break counts as
 * work (Art. 56), so it does not use the allowance. Falls back to the server's figure when the
 * day came without its breaks.
 */
export function breakUsedSeconds(today: Today, now: number): number {
  if (!today.breaks) return (today.break_used_minutes ?? 0) * SECONDS_PER_MINUTE;
  const ms = today.breaks
    .filter((entry) => entry.kind === "regular")
    .reduce((total, entry) => total + lengthOf(spanOf(entry, now)), 0);
  return Math.floor(ms / MS_PER_SECOND);
}

/** Regular break minutes used today (whole minutes of `breakUsedSeconds`). */
export function breakUsedMinutes(today: Today, now: number): number {
  return Math.floor(breakUsedSeconds(today, now) / SECONDS_PER_MINUTE);
}

/** Minutes of the allowance still free today; 0 once it has run out. */
export function breakLeftMinutes(used: number, allowance: number): number {
  return Math.max(0, allowance - used);
}

/** Whether the breaks ran past the allowance: shown gently, flagged for HR, never deducted. */
export function breakOverrun(used: number, allowance: number): boolean {
  return used > allowance;
}

/**
 * Seconds worked today, live: the day's sessions less its regular breaks, exactly as the server
 * counts the day. The work clock therefore stands still during a break and picks up from the
 * same point when the break ends.
 */
export function workedSeconds(today: Today, now: number): number {
  const sessions = [...today.sessions];
  const open = today.open_session;
  if (open && !sessions.some((session) => session.id === open.id)) sessions.push(open);
  const worked = sessions.filter((session) => !session.unpaired).map((s) => spanOf(s, now));
  const breaks = (today.breaks ?? [])
    .filter((entry) => entry.kind === "regular")
    .map((entry) => spanOf(entry, now));
  const ms = worked.reduce((total, session) => {
    const paused = breaks.reduce(
      (sum, pause) =>
        sum +
        lengthOf({
          start: Math.max(pause.start, session.start),
          end: Math.min(pause.end, session.end),
        }),
      0,
    );
    return total + Math.max(0, lengthOf(session) - paused);
  }, 0);
  return Math.floor(ms / MS_PER_SECOND);
}
